import React from 'react';
import { graphql } from 'react-relay';
import { Doughnut, Pie, Bar } from 'react-chartjs-2';
import dayjs from 'dayjs';
import useLiveFragment from '../../../relay/useLiveFragment';
import classes from './HistoryCharts.module.scss';
import {
  HistoryCharts_history$data,
  HistoryCharts_history$key,
} from './__generated__/HistoryCharts_history.graphql';
import { notifyAirbrake } from '../ErrorBoundary/ErrorBoundary';
import LoadingIndicator from '../../atoms/LoadingIndicator/LoadingIndicator';
import A from '../../atoms/A/A';
import Txt from '../../atoms/Txt/Txt';
import useTranslate from '../../../hooks/useTranslate';
import {
  Chart,
  ArcElement,
  LinearScale,
  CategoryScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';
import clsx from 'clsx';
import { parse } from 'node:path/win32';

Chart.register(ArcElement);
Chart.register(LinearScale);
Chart.register(CategoryScale);
Chart.register(BarElement);
Chart.register(Tooltip);
Chart.register(Legend);

export interface Props {
  historyRef: HistoryCharts_history$key;
  currencySymbol: string;
}
const fragmentSpec = graphql`
  fragment HistoryCharts_history on History @refetchable(queryName: "HistoryRefetchQuery") {
    id
    status
    earningTotal {
      cents
      formatted
    }
    cashoutTotal {
      formatted
    }
    earningBreakdown {
      type
      label
      amount {
        cents
        currency
        formatted
      }
    }
    cashoutBreakdown {
      type
      label
      amount {
        cents
        currency
        formatted
      }
    }
    monthlyEarnings {
      date
      type
      amount {
        cents
        currency
        formatted
      }
    }
  }
`;

const constructChartData = (datapoints: number[], labels: string[], chartColor: string[]) => {
  const data = {
    labels,
    datasets: [
      {
        data: datapoints,
        backgroundColor: chartColor,
        borderWidth: 1,
        pointStyle: 'circle',
      },
    ],
  };

  return data;
};

interface Graph {
  [key: string]: string;
}

interface Earning {
  type: string;
  date: string;
  amount: {
    cents: number;
    currency: string;
    formatted: string;
  };
}

function capitalizeFirstLetter(string: string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

function groupBy(monthlyEarnings: ReadonlyArray<Earning>, key: string) {
  /* eslint-disable no-param-reassign */
  return monthlyEarnings.reduce((rv: any, x: any) => {
    (rv[x[key]] = rv[x[key]] || []).push(x);
    return rv;
  }, {});
  /* eslint-enable no-param-reassign */
}

function filterUniqueAndSortDate(dates: string[]) {
  return dates
    .filter((value: string, index: number, self: string[]) => self.indexOf(value) === index)
    .sort((o1: string, o2: string) => {
      if (o1 < o2) {
        return -1;
      }
      if (o1 > o2) {
        return 1;
      }
      return 0;
    });
}
const COLORS = ['#2bd675', '#ff3366', '#ffcf33', '#3399FF', '#495867', '#bc7100'];

const HistoryCharts: React.FC<Props> = ({ historyRef, currencySymbol }) => {
  const history: HistoryCharts_history$data = useLiveFragment(fragmentSpec, historyRef);
  const translate = useTranslate();

  const sortedEarnings =
    history?.earningBreakdown &&
    [...history?.earningBreakdown].sort((a, b) => b.amount.cents - a.amount.cents);
  const sortedCashouts =
    history?.cashoutBreakdown &&
    [...history?.cashoutBreakdown].sort((a, b) => b.amount.cents - a.amount.cents);
  const [earningDataPoints, earningLabels, earningChartColor] = [
    sortedEarnings?.map((earning) => earning.amount.cents / 100) || [],
    sortedEarnings?.map((earning) => translate(capitalizeFirstLetter(earning.type))) || [],
    sortedEarnings?.map((earning, i) => COLORS[i % COLORS.length]) || [],
  ];
  const [cashoutDataPoints, cashoutLabels, cashoutChartColor] = [
    sortedCashouts?.map((earning) => earning.amount.cents / 100) || [],
    sortedCashouts?.map((earning) => translate(capitalizeFirstLetter(earning.type))) || [],
    sortedCashouts?.map((earning, i) => COLORS[i % COLORS.length]) || [],
  ];

  const earningData = constructChartData(earningDataPoints, earningLabels, earningChartColor);

  const doughnutOptions = {
    maintainAspectRatio: false,
    responsive: false,
  };
  function buildPluginOptions(legendPosition: 'left' | 'right' | 'bottom') {
    return {
      tooltip: {
        callbacks: {
          label(context: any) {
            return `${translate(
              context.dataset.label || context.label,
            )} ${currencySymbol}${context.raw.toFixed(2)}`;
          },
        },
      },
      legend: {
        position: legendPosition,
        labels: {
          usePointStyle: true,
        },
      },
    };
  }
  const mapMonthlyDataToChartData = React.useCallback(
    (monthlyEarnings: ReadonlyArray<Earning>) => {
      const groupedMonthlyEarnings = groupBy(monthlyEarnings, 'type');
      const earningTypeKeys = Object.keys(groupedMonthlyEarnings); //.sort((a,b) => );
      const dates = filterUniqueAndSortDate(monthlyEarnings.map((earning) => earning.date));
      const data = {
        labels: dates.map((date) => dayjs(date).format('MM/YY')),
        datasets: earningTypeKeys.map((key) => ({
          label: translate(capitalizeFirstLetter(key)),
          data: dates.map(
            (date) =>
              groupedMonthlyEarnings[key].find((earning: Earning) => earning.date === date)?.amount
                ?.cents / 100 || 0,
          ),
          backgroundColor:
            COLORS[
              (sortedEarnings?.findIndex((earning) => earning.type === key) || 0) % COLORS.length
            ],
        })),
      };
      return data;
    },
    [sortedEarnings, translate],
  );

  const cashoutData = constructChartData(cashoutDataPoints, cashoutLabels, cashoutChartColor);

  const monthlyData = React.useMemo(() => {
    if (history.monthlyEarnings) {
      return mapMonthlyDataToChartData(history.monthlyEarnings);
    }
    return null;
  }, [history, mapMonthlyDataToChartData]);

  const stackedOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        grid: {
          display: false,
        },
        ticks: {
          callback(value: number | string) {
            return `${currencySymbol}${
              typeof value === 'number' ? value.toFixed(2) : parseFloat(value).toFixed(2)
            }`;
          },
        },
        stacked: true,
      },
      x: {
        grid: {
          display: false,
        },
        stacked: true,
      },
    },
    plugins: buildPluginOptions('bottom'),
  };

  // there is no history to display.
  if (history?.status === 'valid' && (!history.earningTotal || history.earningTotal.cents === 0)) {
    return null;
  }

  if (history?.status === 'valid') {
    return (
      <div className={classes.root}>
        <div className={classes.graphContainer}>
          <div className={classes.container}>
            <Txt
              component="h4"
              translateOptions={{ vars: [history.earningTotal?.formatted || ''] }}
            >
              Total Earnings: %1s;
            </Txt>
            <Doughnut
              data={earningData}
              width={256 + 64}
              height={128}
              options={{
                ...doughnutOptions,
                plugins: buildPluginOptions('right'),
              }}
            />
          </div>
          <div className={classes.container}>
            <Txt
              component="h4"
              translateOptions={{ vars: [history.cashoutTotal?.formatted || ''] }}
            >
              Total Cashouts: %1s;
            </Txt>
            <Doughnut
              data={cashoutData}
              width={256 + 64}
              height={128}
              options={{
                ...doughnutOptions,
                plugins: buildPluginOptions('right'),
              }}
            />
          </div>
        </div>
        {monthlyData && (
          <div className={clsx(classes.container, classes.barChartContainer)}>
            <Txt component="h4">Monthly Earnings:</Txt>
            <div className={classes.barChart}>
              <Bar data={monthlyData} options={stackedOptions} />
            </div>
          </div>
        )}
      </div>
    );
  }
  if (history?.status === 'empty' || history?.status === 'processing') {
    return (
      <div className={classes.root}>
        <Txt component="h4">Loading History</Txt>
        <LoadingIndicator />
      </div>
    );
  }
  notifyAirbrake({ error: Error(`unexpected history status ${history.status}`), params: {} });
  return (
    <div className={classes.root}>
      <Txt component="h4">
        Something went wrong displaying your history! Please contact{' '}
        <A to="mailto:support@qmee.com">support@qmee.com</A>
      </Txt>
    </div>
  );
};

export default React.memo(HistoryCharts);
